import type { PageModules } from "@for-it/web-lib";
import type { ICookie } from "cookie-universal";
import {
    ErrorPage,
    ErrorProvider,
    LoadingPage,
    ModalProvider,
    NotFoundPage,
    PagesProvider,
    RoutingProvider,
    RpcClientImplementation,
    RpcClientProvider,
    parseRoutes,
} from "@for-it/web-lib";
import { DomainErrors } from "app-domain";
import { StrictMode, useMemo } from "react";
import { Toaster } from "react-hot-toast";
import { API_HOST } from "./environment.js";
import "@for-it/web-lib/style.css";
import "./index.css";
import { errorMessages } from "./error-messages.js";
import { Routes } from "./routes.js";
import { RouteChangeTracker } from "./utils/ga/router-change-provider.js";
import { UserStageProvider } from "./utils/user-stage-hook.js";

const pageModules = import.meta.glob("./pages/**/*.tsx") as PageModules;

const generatedRoutes = parseRoutes(Routes, pageModules);

export interface AppProps {
    url: string;
    cookies: ICookie;
}

export default function App({ url, cookies }: AppProps) {
    const rpcClient = useMemo(
        () =>
            new RpcClientImplementation(
                {
                    apiHost: API_HOST,
                    errorClasses: DomainErrors,
                },
                cookies,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <StrictMode>
            <RoutingProvider startingURL={url}>
                <RouteChangeTracker />
                <ErrorProvider errorMessages={errorMessages}>
                    <RpcClientProvider rpcService={rpcClient} cookies={cookies}>
                        <UserStageProvider>
                            <ModalProvider>
                                <PagesProvider
                                    LoadingPage={LoadingPage}
                                    routes={generatedRoutes}
                                    ErrorPage={ErrorPage}
                                    NotFoundPage={NotFoundPage}
                                />
                            </ModalProvider>
                            <Toaster />
                        </UserStageProvider>
                    </RpcClientProvider>
                </ErrorProvider>
            </RoutingProvider>
        </StrictMode>
    );
}
